
import { mapState, mapMutations } from 'vuex'

export default {
  props: {
    disableRedirect: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      rememberChoice: false,
      selectedLocation: 'recent',
      selectedDeliveryAddress: null,
      locations: {
        nearest: {},
        recent: {}
      },
      deliveryAddresses: [],
      isLoading: true,
      geolocationError: null,
      showNearestLocation: false,
      hasRecentLocation: false
    }
  },
  computed: {
    ...mapState(['location', 'locationList', 'user', 'order', 'confirmLocationModalSection']),
    redirectUrl() {
      // If there's a pending reorder, go to checkout
      if (this.$store.state.pendingReorderId) {
        return '/order/checkout/'
      }
      if (this.confirmLocationModalSection === 'order-again') {
        return '/order/checkout/'
      }
      return '/order'
    },
    shouldShowRadarMap() {
      // return Object.keys(this.selectedLocationMap).length > 0
      // Added to fix bug with delivery orders
      const loc = this.selectedLocationMap
      return loc && typeof loc === 'object' && 'lat' in loc && 'lng' in loc
    },
    isDelivery() {
      return this.order && this.order.handoff === 'dispatch';
    },
    formattedNearestLocation() {
      const loc = this.locations.nearest;
      if (!loc.address) return 'Loading...';
      return loc.address && loc.city ? `${loc.address}, ${loc.city}, ${loc.state} ${loc.zip}` : 'Loading...';
    },
    formattedRecentLocation() {
      const loc = this.locations.recent;
      if (!loc.address) return 'No recent location';
      return loc.address && loc.city ? `${loc.address}, ${loc.city}, ${loc.state} ${loc.zip}` : 'No recent location';
    },
    selectedLocationMap() {
      if (this.isDelivery) {
        // This is causing an error with delivery orders
        // return this.deliveryAddresses.find(addr => addr.id === this.selectedDeliveryAddress).address
        // console.log(this.deliveryAddresses)
        // console.log(this.selectedDeliveryAddress)
        const selected = this.deliveryAddresses.find(addr => addr.id === this.selectedDeliveryAddress)
        return selected?.address || {}
      } else {
        return this.locations[this.selectedLocation]
      }
    }
  },
  async mounted() {
    // console.log('LocationConfirmModal mounted, disableRedirect:', this.disableRedirect)
    this.isLoading = true;

    if (this.isDelivery) {
      this.loadDeliveryAddresses();

      if (this.deliveryAddresses.length === 0) {
        // console.log('No delivery addresses, calling redirectToOrderPage')
        if (!this.disableRedirect) {
          this.redirectToOrderPage();
        }
        return;
      }
    } else {
      this.getRecentLocation();

      if (!this.hasRecentLocation) {
        // console.log('No recent location, calling redirectToOrderPage')
        if (!this.disableRedirect) {
          this.redirectToOrderPage();
        }
        return;
      }

      this.getUserLocation();
    }

    this.$nextTick(() => {
      const locationConfirmModal = this.$refs.locationConfirmModal;
      if (locationConfirmModal) {
        locationConfirmModal.focus();
      }
      document.addEventListener('keydown', this.trapFocus);
      // need to scroll top due to the focus behavior
      window.scrollTo(0, 0);
    });
  },
  methods: {
    ...mapMutations(['setShouldOpenConfirmLocationModal', 'setConfirmLocationModalSection']),
    close() {
      document.removeEventListener('keydown', this.trapFocus);
      this.setShouldOpenConfirmLocationModal(false)
      this.setConfirmLocationModalSection(null)
      this.$emit('close')
    },
    redirectToOrderPage() {
      // console.log('redirectToOrderPage called, disableRedirect:', this.disableRedirect)
      if (this.disableRedirect) {
        // console.log('Redirect disabled, returning early')
        return
      }
      // console.log('Redirecting to:', this.redirectUrl)
      this.$nextTick(() => {
        this.$router.push(this.redirectUrl)
      })
    },
    async handleContinue() {
      if (this.rememberChoice) {
        this.$cookiz.set("location-confirmed", "true");
      }

      if (this.isDelivery) {
        if (this.selectedDeliveryAddress) {
          await this.processDeliveryLocation();
        }
      } else {
        await this.startPickupOrder()
      }

      this.close();
    },
    async startPickupOrder() {
      const selectedLocationData = this.locations[this.selectedLocation];
      if (!selectedLocationData || !selectedLocationData.id) {
        console.error('❌ [Modal] No location selected')
        return
      }

      console.log('🔄 [Modal] Starting pickup order for location:', selectedLocationData.id, 'menu_id:', selectedLocationData.menu_id)

      // Set location in store first
      this.$store.commit('setLocation', selectedLocationData);
      
      // Wait for store update to complete
      await this.$nextTick()
      
      // Verify location is set correctly
      const currentLocation = this.$store.state.location
      if (!currentLocation || !currentLocation.menu_id) {
        console.error('❌ [Modal] Location not properly set in store:', currentLocation)
        return
      }

      console.log('✅ [Modal] Location set in store. Menu ID:', currentLocation.menu_id)

      // Get menu using the menu_id from the newly set location
      console.log('🔄 [Modal] Fetching menu with ID:', currentLocation.menu_id)
      
      const menuResult = await this.$api.getMenu(true, { 
        menuId: currentLocation.menu_id,
        setMenu: true 
      })
      
      console.log('📦 [Modal] getMenu() result:', menuResult)
      
      if (!menuResult) {
        console.error('❌ [Modal] getMenu() returned false - API request failed')
        return
      }

      // Double-check menu is in store
      const menuInStore = this.$store.state.menu
      console.log('📦 [Modal] Menu in store after getMenu():', menuInStore ? { id: menuInStore.id, categories: menuInStore.categories?.length } : null)

      if (!menuInStore || !menuInStore.categories || menuInStore.categories.length === 0) {
        console.error('❌ [Modal] Menu not valid in store after getMenu() call:', menuInStore)
        return
      }
      
      console.log('✅ [Modal] Menu validated successfully:', { id: menuInStore.id, categories: menuInStore.categories.length })

      // Handle pending reorder or regular order creation
      const pendingReorderId = this.$store.state.pendingReorderId
      if (pendingReorderId) {
        console.log('🔄 [Modal] Processing pending reorder:', pendingReorderId)
        await this.$api.createReorder(pendingReorderId)
        this.$store.commit('setPendingReorderId', null)
        
        // Transfer order if menu changed (e.g., delivery reorder → pickup location)
        const currentOrder = this.$store.state.order
        if (currentOrder && currentOrder.menu_id !== this.location.menu_id) {
          console.log('🔄 [Modal] Transferring reorder to new location menu')
          await this.$api.transferOrder()
        }
      } else if (this.order) {
        if (this.order.menu_id != this.location.menu_id) {
          console.log('🔄 [Modal] Transferring order to new location')
          await this.$api.transferOrder()
        }
      } else {
        console.log('🔄 [Modal] Creating new order')
        await this.$api.createOrder()
      }

      // Track location selection
      window.dataLayer = window.dataLayer || []
      window.dataLayer.push({
        'event': 'select_location',
        'location_name': this.location.address,
        'location_address': this.location.name,
      })

      // Ensure menu is available before redirecting
      if (!this.disableRedirect) {
        // Wait for all store updates to complete
        await this.$nextTick()
        
        // Final check: menu must be in store with categories
        const finalMenu = this.$store.state.menu
        if (finalMenu && finalMenu.categories && finalMenu.categories.length > 0) {
          console.log('✅ [Modal] Menu ready for redirect (ID:', finalMenu.id, 'Categories:', finalMenu.categories.length, '), redirecting to:', this.redirectUrl)
          this.redirectToOrderPage()
        } else {
          console.error('❌ [Modal] Menu not valid after all checks, not redirecting. Store state:', this.$store.state.menu)
        }
      }
    },
    getRecentLocation() {
      if (this.location) {
        this.locations.recent = this.location
        this.hasRecentLocation = true;
        this.selectedLocation = 'recent'; // Default to recent location
      } else {
        this.hasRecentLocation = false;
      }
    },
    getUserLocation() {
      if (!navigator.geolocation) {
        this.isLoading = false;
        return;
      }

      this.isLoading = true;

      navigator.geolocation.getCurrentPosition(
        async position =>  {
          await this.findNearestLocation(position.coords.latitude, position.coords.longitude);
        },
        error => {
          this.isLoading = false;
        },
      );
    },
    async findNearestLocation(lat, lng) {
      await this.$api.findLocations({ lat, lng })

      if (this.locationList.length > 0) {
        this.locations.nearest = this.locationList[0]
        this.showNearestLocation = this.isNearestLocationDifferent()
      }

      this.isLoading = false;
    },
    isNearestLocationDifferent() {
      if (!this.locations.nearest.id || !this.locations.recent.id) {
        return false;
      }

      return this.locations.nearest.id !== this.locations.recent.id;
    },
    loadDeliveryAddresses() {
      this.deliveryAddresses = [];

      if (this.order && this.order.address) {
        this.deliveryAddresses.push(this.order.address);
        this.selectedDeliveryAddress = this.order.address.id;
      }

      if (this.user && this.user.addresses && this.user.addresses.length > 0) {
        const userAddress = this.user.addresses[0];

        if (!this.deliveryAddresses.length || this.deliveryAddresses[0].id !== userAddress.id) {
          this.deliveryAddresses.push(userAddress);

          if (!this.selectedDeliveryAddress) {
            this.selectedDeliveryAddress = userAddress.id;
          }
        }
      }

      this.isLoading = false;
    },
    formatDeliveryAddress(address) {
      if (!address) return 'No address';

      let formattedAddress = address.address || '';
      if (address.apt) formattedAddress += `, ${address.apt}`;
      if (address.city) formattedAddress += `, ${address.city}`;
      if (address.state) formattedAddress += `, ${address.state}`;
      if (address.zip) formattedAddress += ` ${address.zip}`;

      return formattedAddress || 'Invalid address format';
    },
    async processDeliveryLocation() {
      const selectedAddress = this.deliveryAddresses.find(addr => addr.id === this.selectedDeliveryAddress);
      if (!selectedAddress) {
        console.error('No delivery address selected')
        if (!this.disableRedirect) {
          this.$router.push("/locations?mode=delivery")
        }
        return;
      }

      const request = await this.$api.findDeliveryLocations(selectedAddress)
      if (!request || (request.location && request.location.length < 1)) {
        console.error('No delivery locations found for address')
        if (!this.disableRedirect) {
          this.$router.push("/locations?mode=delivery")
        }
        return;
      }

      // Get and set location
      const location = await this.$api.getLocation(request.id)
      if (!location || !location.menu_id) {
        console.error('Invalid location data')
        return
      }

      this.$store.commit('setLocation', location)
      await this.$nextTick()

      // Get menu for delivery location
      const menu = await this.$api.getMenu(true, { 
        menuId: location.menu_id,
        setMenu: true 
      })

      if (!menu) {
        console.error('Failed to load menu for delivery location')
        return
      }

      await this.startDeliveryOrder(selectedAddress)
    },
    async startDeliveryOrder(selectedAddress) {
      // Handle order creation or address update
      if (this.order && this.order.address && this.order.address.id === selectedAddress.id) {
        // Order already has correct address, just redirect
        if (!this.disableRedirect) {
          await this.$nextTick()
          
          // Ensure menu is loaded before redirecting
          const menu = this.$store.state.menu
          if (menu && menu.categories && menu.categories.length > 0) {
            console.log('✅ Delivery order ready, redirecting to:', this.redirectUrl)
            this.redirectToOrderPage()
          } else {
            console.error('❌ Menu not valid, not redirecting:', menu)
          }
        }
      } else {
        let order = null
        
        if (this.order) {
          order = await this.$api.setAddress('dispatch', selectedAddress)
        } else {
          order = await this.$api.createOrder()
          if (order) {
            order = await this.$api.setAddress('dispatch', selectedAddress)
          }
        }
        
        if (order) {
          this.$store.commit('setOrder', order)
          await this.$nextTick()
          
          if (!this.disableRedirect) {
            // Final check: menu and order must be ready
            const menu = this.$store.state.menu
            if (menu && menu.categories && menu.categories.length > 0 && this.$store.state.order) {
              console.log('✅ Delivery order created, redirecting to:', this.redirectUrl)
              this.redirectToOrderPage()
            } else {
              console.error('❌ Menu or order not ready, not redirecting. Menu:', menu, 'Order:', this.$store.state.order)
            }
          }
        } else {
          console.error('Failed to create or update delivery order')
        }
      }
    },
    trapFocus(e) {
      const focusableElements = this.$refs.locationConfirmModal.querySelectorAll(
        'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
      );
      const firstFocusableElement = focusableElements[0];
      const lastFocusableElement = focusableElements[focusableElements.length - 1];

      if (e.key === 'Tab') {
        if (e.shiftKey) /* shift + tab */ {
          if (document.activeElement === firstFocusableElement) {
            e.preventDefault();
            lastFocusableElement.focus();
          }
        } else /* tab */ {
          if (document.activeElement === lastFocusableElement) {
            e.preventDefault();
            firstFocusableElement.focus();
          }
        }
      }

      if (e.key === 'Escape') {
        this.close();
      }
    }
  },
  beforeDestroy() {
    document.removeEventListener('keydown', this.trapFocus);
  }
}
